import {
  Alert,
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import { usePreflightDeactivateSiteGroupsMutation } from '@sophocles/features/entities/siteGroups';

export default function BulkDeactivateSiteGroupDialog({
  open,
  onClose,
  onConfirm,
  siteGroupIds,
  siteGroupRows,
  selectAll = false,
  filters = {},
  customerId,
  isBillboard,
}) {
  const { t } = useTranslation();
  const [radioAction, setRadioAction] = useState('customer_inventory');
  const [destinationId, setDestinationId] = useState(null);
  const [error, setError] = useState(false);
  const [preflightDeactivate, { data: preflight, isLoading, reset }] = usePreflightDeactivateSiteGroupsMutation();

  const sgTerm = isBillboard ? 'plant' : 'site group';
  const sgTermPlural = isBillboard ? 'plants' : 'site groups';
  const siteTerm = isBillboard ? 'structure' : 'site';
  const siteTermPlural = isBillboard ? 'structures' : 'sites';

  // Clearing the last run is its own effect, keyed on the close transition.
  // `reset` is a new function on every trigger — RTK rebuilds it whenever the
  // mutation's promise or requestId changes — so an effect that both fires the
  // preflight and depends on `reset` re-fires itself for as long as the dialog
  // is open.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      reset();
      setRadioAction('customer_inventory');
      setDestinationId(null);
      setError(false);
    }
    wasOpenRef.current = open;
  }, [open, reset]);

  // Fetch preflight data when dialog opens.
  useEffect(() => {
    if (!open) return;
    if (!selectAll && siteGroupIds.length === 0) return;

    preflightDeactivate(selectAll ? { selectAll: true, filters } : { siteGroupIds });
  }, [open, siteGroupIds, selectAll, filters, preflightDeactivate]);

  const hasUnits = preflight?.total_units > 0;
  const hasVirtual = preflight?.virtual_count > 0;
  const isRelocate = radioAction === 'relocate_sites' || radioAction === 'relocate_radios';
  const confirmDisabled = isLoading || !preflight || (hasUnits && isRelocate && !destinationId);

  const handleConfirm = useCallback(() => {
    if (hasUnits && !radioAction) {
      setError(true);
      return;
    }
    const relocate = radioAction === 'relocate_sites' || radioAction === 'relocate_radios';
    if (relocate && !destinationId) {
      setError(true);
      return;
    }
    onConfirm({
      radio_action: radioAction,
      destination_site_group_id: relocate ? destinationId : null,
    });
  }, [hasUnits, radioAction, destinationId, onConfirm]);

  const totalCount = preflight?.site_groups ?? siteGroupIds.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Deactivate {selectAll ? `all ${totalCount}` : totalCount} {totalCount === 1 ? sgTerm : sgTermPlural}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : preflight ? (
          <Box>
            {/* List of site groups (only for individual selection, not select-all) */}
            {!selectAll && siteGroupRows.length > 0 && siteGroupRows.length <= 20 && (
              <Box sx={{ mb: 2 }}>
                {siteGroupRows.map(row => (
                  <Box key={row.id} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.name}
                      {row.is_virtual && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (virtual)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Summary of what will happen */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('siteGroups.bulkDeactivateSummary', 'This action will:')}
              </Typography>

              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li" variant="body2">
                  Deactivate {totalCount} {totalCount === 1 ? sgTerm : sgTermPlural}
                </Typography>

                {preflight.total_active_sites > 0 && (
                  <Typography component="li" variant="body2">
                    Deactivate {preflight.total_active_sites} active{' '}
                    {preflight.total_active_sites === 1 ? siteTerm : siteTermPlural}
                  </Typography>
                )}

                {hasUnits && (
                  <Typography component="li" variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                    Affect {preflight.total_units} {preflight.total_units === 1 ? 'unit' : 'units'} with attached radios
                  </Typography>
                )}

                {hasVirtual && (
                  <Typography component="li" variant="body2">
                    Remove {preflight.virtual_count} virtual {preflight.virtual_count === 1 ? sgTerm : sgTermPlural}{' '}
                    (shared {siteTermPlural} will be unlinked, not deactivated)
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Warning for virtual groups */}
            {hasVirtual && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Virtual {sgTermPlural} only hold shared references. Their {siteTermPlural} belong to other{' '}
                {sgTermPlural} and will not be deactivated.
              </Alert>
            )}

            {/* Warning for cellular SIMs */}
            {preflight.has_cellular && radioAction === 'purge' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some units have cellular SIMs that will be deactivated. This cannot be undone.
              </Alert>
            )}

            {/* Radio action options - only show when units exist */}
            {hasUnits && (
              <FormControl component="fieldset" error={error} sx={{ mt: 1, width: '100%' }}>
                <FormLabel component="legend">
                  {t('siteGroups.whatShouldHappenToUnits', 'What should happen to the units?')}
                </FormLabel>
                <RadioGroup
                  value={radioAction}
                  onChange={e => {
                    setRadioAction(e.target.value);
                    setError(false);
                  }}
                >
                  <FormControlLabel
                    value="customer_inventory"
                    control={<Radio />}
                    label={
                      <div>
                        <b>{t('siteGroups.returnToCustomerInventory', 'Return to Customer Inventory')}</b> -{' '}
                        <span>
                          {t(
                            'siteGroups.returnToCustomerInventoryDescription',
                            'Units will be moved to customer-level inventory and can be re-installed later.',
                          )}
                        </span>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="purge"
                    control={<Radio />}
                    label={
                      <div>
                        <b>{t('sites.retire', 'Retire')}</b> -{' '}
                        <span>
                          {t(
                            'sites.retireDescriptionPlain',
                            'Deactivated and removed from this account. The device cannot be reactivated for future use.',
                          )}
                        </span>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="relocate_sites"
                    control={<Radio />}
                    label={
                      <div>
                        <b>{t('siteGroups.relocateSites', 'Relocate Sites')}</b> -{' '}
                        <span>
                          {t(
                            'siteGroups.relocateSitesDescription',
                            'Transfer active sites (with their units) to another site group. Sites stay active.',
                          )}
                        </span>
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="relocate_radios"
                    control={<Radio />}
                    label={
                      <div>
                        <b>{t('siteGroups.relocateRadios', 'Relocate Radios Only')}</b> -{' '}
                        <span>
                          {t(
                            'siteGroups.relocateRadiosDescription',
                            "Deactivate sites and move radios to another site group's inventory.",
                          )}
                        </span>
                      </div>
                    }
                  />
                </RadioGroup>

                {/* Destination picker for relocate options */}
                {isRelocate && (
                  <Box sx={{ mt: 2, ml: 4 }}>
                    <SiteGroupAutocomplete
                      customerId={customerId}
                      name="destination_site_group"
                      value={destinationId}
                      label={`Destination ${sgTerm}`}
                      fullWidth
                      disablePortal={false}
                      filters={{ is_virtual: false }}
                      ignoreIds={siteGroupIds}
                      error={error && !destinationId}
                      helperText={error && !destinationId ? 'Please select a destination.' : ''}
                      onChange={(e, siteGroupId) => {
                        setDestinationId(siteGroupId);
                        setError(false);
                      }}
                    />
                  </Box>
                )}

                {error && !isRelocate && (
                  <Typography sx={{ color: red[500], mt: 1 }}>
                    {t('siteGroups.selectOptionFirst', 'Please select an option before proceeding.')}
                  </Typography>
                )}
              </FormControl>
            )}
          </Box>
        ) : (
          <Typography color="error">
            {t('siteGroups.preflightLoadFailed', 'Failed to load preflight data. Please try again.')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={confirmDisabled}>
          {t('actions.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
